import { useState } from 'react'
import type { NoteCustomer } from '@/lib/voiceNotes'
import { useCustomerSearch, type CustomerMatch } from '@/hooks/useCustomerSearch'
import { cn } from '@/lib/utils'
import { Spinner } from '@/components/Spinner'

// Pins WHO this chat is about — general context, not note-specific: after
// picking, every customer-scoped ask (bookings, payments, notes, attachments)
// refers to this person until the operator names another. The selection rides
// with every operator send (v1-chat injects the customer into the system
// prompt and saves media attachments to their record), so the AI never has to
// guess the subject. Session-scoped on purpose — a stale pin from yesterday
// attributing today's work to the wrong person is worse than re-picking.

interface CustomerPickerProps {
  selected: NoteCustomer | null
  onSelect: (customer: NoteCustomer | null) => void
  disabled?: boolean
}

export function CustomerPicker({ selected, onSelect, disabled }: CustomerPickerProps) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const { matches, searching, active } = useCustomerSearch(query, { enabled: open })

  const chipClass =
    'rounded-2xl border border-border bg-muted px-3 py-1.5 text-[13px] font-medium text-muted-foreground disabled:opacity-40'

  const choose = (m: CustomerMatch) => {
    onSelect({ id: m.id, name: m.name ?? 'Unnamed customer' })
    setOpen(false)
    setQuery('')
  }

  const unpin = () => {
    onSelect(null)
    setOpen(false)
    setQuery('')
  }

  return (
    <div className="relative">
      {open && (
        <>
          <button
            type="button"
            className="fixed inset-0 z-10"
            aria-label="Close customer menu"
            onClick={() => setOpen(false)}
          />
          <div className="absolute bottom-full left-0 z-20 mb-1.5 flex max-h-80 w-64 flex-col overflow-hidden rounded-lg border border-border bg-card shadow-[0_4px_12px_rgba(0,0,0,0.10)]">
            <div className="border-b border-muted p-2">
              <input
                type="text"
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search customers…"
                aria-label="Search customers"
                className="w-full rounded-md border border-border bg-background px-2.5 py-1.5 text-[14px] text-foreground outline-none placeholder:text-muted-foreground"
              />
            </div>
            {/* Unpinning must never be something you scroll to find: outside the
                scroll area, so a list of matches can't push it out of view. */}
            {selected && (
              <button
                type="button"
                className="flex w-full cursor-pointer items-center justify-between border-b border-muted bg-muted/40 px-3.5 py-[11px] text-left"
                onClick={unpin}
              >
                <span className="text-[14px] text-foreground">Unpin {selected.name}</span>
                <span className="text-[13px] text-muted-foreground">✕</span>
              </button>
            )}
            <div className="min-h-0 flex-1 overflow-y-auto">
              {searching && (
                <div className="flex items-center gap-2 px-3.5 py-[11px] text-muted-foreground">
                  <Spinner size={14} className="text-current" />
                  <span className="text-[13px]">Searching…</span>
                </div>
              )}
              {!searching && active && matches.length === 0 && (
                <p className="px-3.5 py-[11px] text-[13px] text-muted-foreground">No customers match.</p>
              )}
              {!searching && !active && (
                <p className="px-3.5 py-[11px] text-[13px] text-muted-foreground">
                  Type a name, email, or phone to pick the customer this chat is about.
                </p>
              )}
              {matches.map((m) => (
                <button
                  key={m.id}
                  type="button"
                  className="flex w-full cursor-pointer flex-col border-t border-muted px-3.5 py-[9px] text-left"
                  onClick={() => choose(m)}
                >
                  <span className="truncate text-[14px] text-foreground">{m.name ?? 'Unnamed customer'}</span>
                  {m.phone ? <span className="text-[12px] text-muted-foreground">{m.phone}</span> : null}
                </button>
              ))}
            </div>
          </div>
        </>
      )}
      {/* Pinned, the chip carries its own ✕ — unpinning is a one-click undo of a
          one-click action, not something to go looking for inside a menu. */}
      {selected ? (
        <span
          className={cn(
            chipClass,
            'inline-flex max-w-64 items-center gap-0.5 border-blue-200 bg-blue-50 py-0 pl-3 pr-1 text-blue-700',
          )}
        >
          <button
            type="button"
            className="truncate py-1.5 text-left disabled:opacity-40"
            onClick={() => setOpen((v) => !v)}
            disabled={disabled}
            aria-label="Change which customer this chat is about"
          >
            👤 {selected.name}
          </button>
          <button
            type="button"
            className="rounded-full px-1.5 py-1 text-blue-700/70 disabled:opacity-40"
            onClick={unpin}
            disabled={disabled}
            title="Unpin customer"
            aria-label={`Unpin ${selected.name}`}
          >
            ✕
          </button>
        </span>
      ) : (
        <button
          type="button"
          className={cn(chipClass, 'max-w-56 truncate')}
          onClick={() => setOpen((v) => !v)}
          disabled={disabled}
          aria-label="Choose which customer this chat is about"
        >
          👤 Customer: none
        </button>
      )}
    </div>
  )
}
